import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { RevealOnScroll } from "./Reveal";
import { Link } from "react-router-dom";

// Photographic imagery inherited from the parent Maklan site
// (https://maklan-trade.preview.emergentagent.com/divisions)
const IMAGES = {
  "Maklan Motors":    "https://images.pexels.com/photos/12122221/pexels-photo-12122221.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "Maklan Energy":    "https://images.pexels.com/photos/371917/pexels-photo-371917.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "Maklan Home":      "https://images.pexels.com/photos/12441654/pexels-photo-12441654.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "Maklan Tech":      "https://images.pexels.com/photos/17489157/pexels-photo-17489157.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "Maklan Machinery": "https://images.pexels.com/photos/13931828/pexels-photo-13931828.jpeg?auto=compress&cs=tinysrgb&w=1600",
};

const FALLBACK = [
  { name: "Maklan Motors",    tagline: "Premium mobility, sourced with intent.",        status: "available",  order: "01" },
  { name: "Maklan Energy",    tagline: "Powering the continent, quietly.",              status: "coming_soon", order: "02" },
  { name: "Maklan Home",      tagline: "The quiet luxury of a well-made life.",         status: "coming_soon", order: "03" },
  { name: "Maklan Tech",      tagline: "The infrastructure of tomorrow.",               status: "coming_soon", order: "04" },
  { name: "Maklan Machinery", tagline: "Built for the continent's next chapter.",       status: "coming_soon", order: "05" },
];

function DivisionCard({ division, aspect = "aspect-[4/5]", featured = false, index = 0 }) {
  const isAvailable = division.status === "available";
  const isMotors = division.name === "Maklan Motors";
  const image = IMAGES[division.name];

  const inner = (
    <div className={`relative overflow-hidden bg-[#0A1128] ${aspect}`}>
      {image && (
        <img
          alt={division.name}
          loading="lazy"
          src={image}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
        />
      )}
      {/* Bottom navy gradient for text legibility */}
      <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-[#050814]/95 via-[#050814]/60 to-transparent" />
      {/* Top gold radial glow for available */}
      {isAvailable && (
        <div
          className="absolute -top-24 -left-24 w-[360px] h-[360px] rounded-full opacity-25 mix-blend-screen pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(197,160,89,0.55), rgba(197,160,89,0) 70%)" }}
        />
      )}
      {/* Status + order */}
      <div className="absolute top-6 left-6 right-6 flex items-center justify-between">
        <span className={`overline px-3 py-1.5 border ${
          isAvailable ? "text-white border-[#C5A059] bg-[#C5A059]/25" : "text-white/70 border-white/25"
        }`}>
          {isAvailable ? "Available Now" : "Coming Soon"}
        </span>
        <span className="text-white/60 text-xs tracking-[0.28em]">{division.order}</span>
      </div>
      {/* Name + tagline + link */}
      <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
        <div className="overline text-[#C5A059] mb-3">Division</div>
        <h3 className="font-display text-3xl md:text-4xl text-white leading-tight">{division.name}</h3>
        <p className="text-white/70 text-sm mt-3 max-w-sm">{division.tagline}</p>
        <div className="mt-6 flex items-center gap-2 text-white text-[11px] uppercase tracking-[0.28em]">
          <span className="link-under">{isMotors ? "Explore Motors" : "Register Interest"}</span>
          <ArrowUpRight size={14} />
        </div>
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 1.2, delay: index * 0.08, ease: [0.19, 1, 0.22, 1] }}
      className="group relative"
      data-testid={`division-card-${division.name.toLowerCase().replace(/\s+/g,'-')}`}
    >
      {isMotors ? (
        <Link to="/inventory" className="block">{inner}</Link>
      ) : (
        <Link to="/contact" className="block">{inner}</Link>
      )}
    </motion.div>
  );
}

export default function MaklanDivisions() {
  const [divisions, setDivisions] = useState(FALLBACK);
  useEffect(() => {
    api.get("/site/config").then((r) => {
      if (Array.isArray(r.data?.divisions) && r.data.divisions.length) setDivisions(r.data.divisions);
    }).catch(() => {});
  }, []);

  const motors = divisions.find((d) => d.name === "Maklan Motors");
  const others = divisions.filter((d) => d.name !== "Maklan Motors");

  return (
    <section data-testid="maklan-family-section" className="section-y-lg bg-ink border-t border-white/5">
      <div className="container-editorial">
        <div className="grid grid-cols-12 gap-8 md:gap-16 mb-20 md:mb-28">
          <div className="col-span-12 md:col-span-7">
            <motion.span
              initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 1.2, ease: [0.19, 1, 0.22, 1] }}
              className="block h-px bg-[#C5A059] w-14 mb-8 origin-left"
            />
            <div className="overline text-[#C5A059] mb-6">The Maklan House</div>
            <RevealOnScroll
              element="h2"
              className="font-display text-5xl md:text-6xl leading-[0.95] tracking-tight"
              lines={["Five houses.", "One <em>standard</em>."]}
            />
          </div>
          <div className="col-span-12 md:col-span-5 md:pt-6">
            <p className="editorial-body">
              Each Maklan division operates as an independent sourcing corridor —
              staffed with domain specialists, governed by a shared internal standard.
              Motors is the first to open its doors. The rest follow in turn.
            </p>
          </div>
        </div>

        {/* Row 1: Motors dominant (2/3) + Energy (1/3) — mirrors the parent layout */}
        <div className="grid grid-cols-12 gap-6 md:gap-8 mb-6 md:mb-8">
          {motors && (
            <div className="col-span-12 md:col-span-8">
              <DivisionCard division={motors} aspect="aspect-[3/4]" featured index={0} />
            </div>
          )}
          {others[0] && (
            <div className="col-span-12 md:col-span-4">
              <DivisionCard division={others[0]} aspect="aspect-[4/5]" index={1} />
            </div>
          )}
        </div>

        {/* Row 2: remaining 3 divisions in equal columns */}
        <div className="grid grid-cols-12 gap-6 md:gap-8">
          {others.slice(1).map((d, i) => (
            <div key={d.name} className="col-span-12 md:col-span-4">
              <DivisionCard division={d} aspect="aspect-[4/5]" index={i + 2} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
